<template>
	<Col :span="24">
		<TypographyTitle :level="3">{{ $t('settings.profileManagement.header') }}</TypographyTitle>
		<div class="scrollable-x">
			<Table :columns="columns" :data-source="data" :pagination="false" :loading="isLoading">
				<template #bodyCell="{ column, record }">
					<template v-if="column.dataIndex === 'tempPassword'">
						<NoLineBreaks v-if="record.tempPassword">
							<Tooltip :title="$t('settings.profileManagement.generateTempPassword.buttonLabel')">
								<Button shape="circle" size="small" disabled>
									<template #icon><RedoOutlined /></template>
								</Button>
							</Tooltip>
							<span class="pd">{{ record.tempPassword }}</span>
						</NoLineBreaks>
						<PopconfirmButton
							v-else-if="!record.currentUser"
							:ok-text="$t('settings.profileManagement.generateTempPassword.confirm')"
							:cancel-text="$t('settings.profileManagement.generateTempPassword.cancel')"
							:onConfirm="() => onGenerateTempPasswordClick(record.username)"
							size="small"
						>
							<template #title>
								<div>{{ $t('settings.profileManagement.generateTempPassword.title') }}</div>
								<div class="max-width-normal">{{ $t('settings.profileManagement.generateTempPassword.content') }}</div>
							</template>
							<template #icon><RedoOutlined /></template>
							{{ $t('settings.profileManagement.generateTempPassword.buttonLabel') }}
						</PopconfirmButton>
						<TypographyText v-else type="secondary">
							{{ $t('settings.profileManagement.generateTempPassword.disabled') }}
						</TypographyText>
					</template>
					<template v-else-if="column.dataIndex === 'role'">
						<Select v-model:value="record.role" :options="roles" :disabled="record.currentUser || !record.enabled" @change="profileUpdate(record)" class="role-select"/>
					</template>
					<template v-else-if="column.dataIndex === 'connectionLimitNearVrs'">
						<ProfileSettingInput :record="record" @updated="updateList"/>
					</template>
					<template v-else-if="column.dataIndex === 'geoFencingEnabled'">
						<NoLineBreaks v-if="!record.currentUser">
							<Tooltip :title="$t('settings.profileManagement.useGeoFencing.buttonLabel')">
								<Button shape="circle" size="small" @click="onGeoFencingClick(record)">
									<template #icon><SettingOutlined /></template>
								</Button>
							</Tooltip>
							<span class="pd">
								<Tag v-if="record.geoFencingEnabled" color="blue">{{ $t('settings.profileManagement.useGeoFencing.enabled') }}</Tag>
								<Tag v-else>{{ $t('settings.profileManagement.useGeoFencing.disabled') }}</Tag>
							</span>
						</NoLineBreaks>
					</template>
					<template v-else-if="column.dataIndex === 'accessibleBaseStations'">
						<NoLineBreaks v-if="record.role === 'rover' && !record.currentUser">
							<Tooltip :title="$t('settings.profileManagement.accessibleBaseStations.buttonLabel')">
								<Button shape="circle" size="small" @click="onAccessibleBaseStationsClick(record)">
									<template #icon><SettingOutlined /></template>
								</Button>
							</Tooltip>
							<span class="pd">
								<Tag :color="(record.accessibleBaseStations || []).length ? 'blue' : undefined">{{ $t('settings.profileManagement.accessibleBaseStations.status', (record.accessibleBaseStations || []).length) }}</Tag>
							</span>
						</NoLineBreaks>
					</template>
					<template v-else-if="column.dataIndex === 'vrsEnabled'">
						<Switch v-if="record.role === 'rover' && !record.currentUser" v-model:checked="record.vrsEnabled" size="small" :disabled="!record.enabled" @change="profileUpdate(record)" />
					</template>
					<template v-else-if="column.dataIndex === 'nearEnabled'">
						<Switch v-if="record.role === 'rover' && !record.currentUser" v-model:checked="record.nearEnabled" size="small" :disabled="!record.enabled" @change="profileUpdate(record)" />
					</template>
					<template v-else-if="column.dataIndex === 'cssrEnabled'">
						<Switch v-if="record.role === 'rover' && !record.currentUser" v-model:checked="record.cssrEnabled" size="small" :disabled="!record.enabled" @change="profileUpdate(record)" />
					</template>
					<template v-else-if="column.dataIndex === 'brdcEnabled'">
						<Switch v-if="record.role === 'rover' && !record.currentUser" v-model:checked="record.brdcEnabled" size="small" :disabled="!record.enabled" @change="profileUpdate(record)" />
					</template>
					<template v-else-if="column.dataIndex === 'enabled'">
						<Switch v-if="!record.currentUser" v-model:checked="record.enabled" size="small" @change="profileUpdate(record)" />
					</template>
					<template v-else-if="column.dataIndex === 'actions'">
						<PopconfirmButton
							v-if="!record.currentUser"
							placement="topRight"
							:title="$t('settings.profileManagement.deleteConfirmation')"
							:ok-text="$t('settings.profileManagement.deleteButtonLabel')" 
							:cancel-text="$t('settings.profileManagement.cancelButtonLabel')"
							:onConfirm="() => onDeleteProfile(record.username)"
						>
							{{ $t('settings.profileManagement.deleteButtonLabel') }}
						</PopconfirmButton>
					</template>
				</template>
			</Table>
		</div>
		<Button type="primary" @click.prevent="onUserAddButtonClick" class="user-add-button">{{ $t('settings.userAddButtonLabel') }}</Button>
		<AddUserModal v-model="addUserModalIsVisible" :onAdd="onAddUser" />
		<GeoFencingModal v-model="geoFencingModalIsVisible" :profile="geoFencingProfile" :onProfileUpdate="() => updateList()" />
		<AccessibleBaseStationsModal
			v-model="accessibleBaseStationsModalIsVisible"
			:profile="accessibleBaseStationsProfile"
			:stations="accessibleBaseStationOptions"
			:onProfileUpdate="() => updateList()"
		/>
	</Col>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import {
	TypographyTitle, TypographyText, Table, Button, Select,
	Switch, Tooltip, Col, Tag, message
} from 'ant-design-vue';
import { RedoOutlined, SettingOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import TranslationUtils from '@/i18n/TranslationUtils';

import { useUserStore } from '@/stores/user';
import { useLicenseStore } from '@/stores/licenseStore';
import { showErrorMessage } from '@/components/common/errorMessage';
import { useProfileUpdate } from '@/components/settings/profiles/useProfileUpdate';


import AddUserModal from '@/components/settings/profiles/AddUserModal.vue';
import NoLineBreaks from '@/components/common/NoLineBreaks.vue';
import ProfileSettingInput from '@/components/settings/profiles/ProfileSettingInput';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import GeoFencingModal from '@/components/settings/profiles/GeoFencingModal.vue';
import AccessibleBaseStationsModal from '@/components/settings/profiles/AccessibleBaseStationsModal.vue';

export default {
	components: {
		TypographyTitle, TypographyText, Table, Button, Select,
		Switch, Tooltip, Col,
		AddUserModal, NoLineBreaks, ProfileSettingInput,
		RedoOutlined, Tag, SettingOutlined,
		PopconfirmButton,
		GeoFencingModal,
		AccessibleBaseStationsModal,
	},
	setup() {
		const { t } = useI18n();
		const data = ref([]);
		const isLoading = ref(true);
		const addUserModalIsVisible = ref(false);
		const accessibleBaseStationOptions = ref([]);
		const roles = TranslationUtils.addTranslatedField(
			useUserStore().visibleRoles,
			'value', 'label',
			'settings.profileManagement.roleNames',
		)

		const updateList = () => {
			isLoading.value = true;
			Api.profileGetList(responseData => {
				data.value = responseData.profiles.map(Api.deserializeProfile);
				accessibleBaseStationOptions.value = responseData.accessible_base_station_options || [];
				isLoading.value = false;
			});
		};

		const { profileUpdate } = useProfileUpdate(
			() => { },
			() => { },
			updateList,
		);

		const onUserAddButtonClick = () => {
			addUserModalIsVisible.value = true;
		}
		const onAddUser = () => {
			addUserModalIsVisible.value = false;
			updateList();
		}

		const geoFencingModalIsVisible = ref(false);
		const geoFencingProfile = ref(null);
		const onGeoFencingClick = profile => {
			geoFencingModalIsVisible.value = true;
			geoFencingProfile.value = profile;
		}

		const accessibleBaseStationsModalIsVisible = ref(false);
		const accessibleBaseStationsProfile = ref(null);
		const onAccessibleBaseStationsClick = profile => {
			accessibleBaseStationsModalIsVisible.value = true;
			accessibleBaseStationsProfile.value = profile;
		}

		const onGenerateTempPasswordClick = username => {
			Api.generateTempPassword(username, response => {
				if (response.status === 'ok') {
					message.success(t('settings.profileManagement.generateTempPassword.success'));
				} else {
					showErrorMessage(response.errors);
				}
				updateList();
			})
		}

		const onDeleteProfile = username => {
			Api.deleteProfile(username, response => {
				if (response.status === 'ok') {
					message.success(t('settings.profileManagement.deleteSuccess'));
					updateList();
				} else {
					showErrorMessage(response.errors);
				}
			});
		};
		onMounted(updateList);

		const licenseStore = useLicenseStore();
		const columnsSpecification = computed(() => Consts.USER_MANAGEMENT_TABLE_COLUMNS.filter(col => {
			if (col.dataIndex === 'vrsEnabled') {
				return licenseStore.allowVrs;
			}
			if (col.dataIndex === 'cssrEnabled') {
				return licenseStore.allowPppRtk;
			}
			return true;
		}));
		const columns = TranslationUtils.addTranslatedField(
			columnsSpecification,
			'dataIndex', 'title',
			'settings.profileManagement.tableColumns',
		);
		return {
			data,
			roles,
			columns,
			isLoading,
			addUserModalIsVisible,
			geoFencingModalIsVisible,
			geoFencingProfile,
			onGeoFencingClick,
			accessibleBaseStationsModalIsVisible,
			accessibleBaseStationsProfile,
			accessibleBaseStationOptions,
			onAccessibleBaseStationsClick,
			profileUpdate,
			updateList,
			onUserAddButtonClick,
			onAddUser,
			onGenerateTempPasswordClick,
			onDeleteProfile,
		};
	},
}
</script>

<style scoped>
.user-add-button {
	margin-top: 8px;
}

.role-select {
	width: 100%;
}
</style>
